
import React from 'react';
import { Link } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ChatButton } from '@/components/chat/ChatButton';
import { Header } from '@/components/landing/Header';
import { Footer } from '@/components/landing/Footer';
import { BackgroundEffects } from '@/components/landing/BackgroundEffects';

export default function Pricing() {
  const plans = [
    {
      name: "Starter",
      price: "Free",
      period: "forever",
      description: "Perfect for individuals getting started with AI-powered workflows",
      features: [
        "Up to 5 AI conversations per month",
        "Basic lead generation tools",
        "Email templates",
        "Community support",
        "Mobile app access"
      ],
      limitations: [
        "Advanced analytics",
        "Custom integrations",
        "Priority support",
        "Advanced AI models"
      ],
      cta: "Get Started",
      popular: false
    },
    {
      name: "Professional",
      price: "$29",
      period: "per month",
      description: "Ideal for small to medium businesses scaling their operations",
      features: [
        "Unlimited AI conversations",
        "Advanced lead generation",
        "Custom email campaigns",
        "Priority support",
        "Advanced analytics",
        "CRM integrations",
        "Team collaboration",
        "Custom workflows"
      ],
      limitations: [
        "White-label solutions",
        "Enterprise integrations"
      ],
      cta: "Start Free Trial",
      popular: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "contact us",
      description: "For large organizations with complex requirements",
      features: [
        "Everything in Professional",
        "White-label solutions",
        "Custom integrations",
        "Dedicated account manager",
        "24/7 phone support",
        "Custom AI training",
        "Advanced security",
        "SLA guarantees",
        "On-premise deployment"
      ],
      limitations: [],
      cta: "Contact Sales",
      popular: false
    }
  ];

  const faqs = [
    {
      question: "Can I change my plan at any time?",
      answer: "Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle."
    },
    {
      question: "Is there a free trial?",
      answer: "Yes, we offer a 14-day free trial for the Professional plan. No credit card required."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards, PayPal, and bank transfers for enterprise customers."
    },
    {
      question: "Can I cancel my subscription?",
      answer: "Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your billing period."
    },
    {
      question: "Do you offer custom solutions?",
      answer: "Yes, our Enterprise plan includes custom solutions tailored to your specific business needs."
    }
  ];

  return (
    <div className="bg-neutral-900 text-white relative overflow-hidden min-h-screen">
      <BackgroundEffects />
      <Header />
      
      {/* Hero Section */}
      <div className="relative pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Simple, transparent{' '}
            <span className="text-yellow-400 relative">
              pricing
              <div className="absolute -bottom-2 left-0 w-full h-3 bg-yellow-400/20 rounded-full"></div>
            </span>
          </h1>
          <p className="text-xl text-neutral-300 mb-8 max-w-2xl mx-auto">
            Choose the plan that fits your business needs. Start free and scale as you grow.
          </p>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <div 
                key={index} 
                className={`relative bg-neutral-800/50 border rounded-2xl p-8 ${
                  plan.popular 
                    ? 'border-yellow-400 scale-105 shadow-2xl shadow-yellow-400/20' 
                    : 'border-neutral-700/50'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-yellow-400 text-neutral-900 px-4 py-2 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-yellow-400">{plan.price}</span>
                    {plan.period && <span className="text-neutral-400 ml-2">/{plan.period}</span>}
                  </div>
                  <p className="text-neutral-300 text-sm">{plan.description}</p>
                </div>

                <div className="space-y-4 mb-8">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center">
                      <Check className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                      <span className="text-neutral-300">{feature}</span>
                    </div>
                  ))}
                  {plan.limitations.map((limitation, idx) => (
                    <div key={idx} className="flex items-center opacity-50">
                      <X className="w-5 h-5 text-neutral-500 mr-3 flex-shrink-0" />
                      <span className="text-neutral-500">{limitation}</span>
                    </div>
                  ))}
                </div>

                <Button 
                  asChild 
                  className={`w-full ${
                    plan.popular 
                      ? 'bg-yellow-400 text-neutral-900 hover:bg-yellow-300' 
                      : 'bg-neutral-700 text-white hover:bg-neutral-600'
                  }`}
                >
                  <Link to="/register">{plan.cta}</Link>
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-20 px-4 bg-neutral-800/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Frequently asked <span className="text-yellow-400">questions</span>
            </h2>
            <p className="text-xl text-neutral-400">
              Everything you need to know about our pricing and plans
            </p>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-neutral-800/50 border border-neutral-700/50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3">{faq.question}</h3>
                <p className="text-neutral-300 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-neutral-800 to-neutral-900 border border-neutral-700 rounded-3xl p-12 md:p-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to get started?
            </h2>
            <p className="text-xl text-neutral-300 mb-8">
              Join thousands of businesses using atWork.ai to transform their workflows.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-yellow-400 text-neutral-900 hover:bg-yellow-300 px-8 py-4 rounded-full font-semibold">
                <Link to="/register">Start Free Trial</Link>
              </Button>
              <Button variant="outline" size="lg" className="border-neutral-600 text-white hover:bg-neutral-800 px-8 py-4 rounded-full" asChild>
                <Link to="/contact">Contact Sales</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <ChatButton />
    </div>
  );
}
